import { useState, useCallback, useMemo } from "react";
import { FormikProps } from "formik";
import { getVideoIdFromUrl, extractUrlFromEmbed } from "../utils";

interface UseEmbedUrlValidationProps {
  formik: FormikProps<{
    Embed_Url: string;
    AssetType: string;
    Streaming: string;
    EmbedType: string;
  }>;
  embedType: string;
  userSocialChannels: any[];
  mediaMetaApiPlatformMap: Record<string, string>;
  onMediaUpdate?: (embedId: string, platform: string) => Promise<void>;
  onMediaCreation?: (
    data: any,
    embedId: string,
    platform: string
  ) => Promise<void>;
  fromDetails?: boolean;
  MediaMetaApi: (params: any) => Promise<any>;
  toast: any;
}

export const useEmbedUrlValidation = ({
  formik,
  embedType,
  userSocialChannels,
  mediaMetaApiPlatformMap,
  onMediaUpdate,
  onMediaCreation,
  fromDetails = false,
  MediaMetaApi,
  toast,
}: UseEmbedUrlValidationProps) => {
  const [mediaMetadata, setMediaMetadata] = useState<any>(null);
  const [showPlayer, setShowPlayer] = useState<boolean>(false);
  const [isUrlLocked, setIsUrlLocked] = useState(false);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [disableLive, setDisableLive] = useState(false);

  // Memoize Facebook channel lookup
  const facebookChannelId = useMemo(() => {
    return (
      userSocialChannels.find((social: any) => social.platform === "facebook")
        ?.id ?? "abcdef"
    );
  }, [userSocialChannels]);

  const validateUrl = useCallback(
    async (isBlurValidation = false) => {
      if (!formik.values.Embed_Url || formik.errors.Embed_Url) return;

      const platform = mediaMetaApiPlatformMap[embedType];
      const platformForIdExtraction =
        embedType === "0" ? "yt" : embedType === "1" ? "fb" : "hls";
      const embedId = getVideoIdFromUrl(
        platformForIdExtraction,
        formik.values.Embed_Url
      );

      setIsValidatingUrl(true);

      try {
        const res = await MediaMetaApi({
          platform: mediaMetaApiPlatformMap[embedType],
          id: embedId,
          ...(embedType === "1" && {
            channel: facebookChannelId,
          }),
        });

        if (res.error) {
          formik.setFieldTouched("Embed_Url", true, false);
          formik.setFieldError("Embed_Url", res.error);
          if (isBlurValidation) {
            toast.fail("");
          }
          return;
        }

        if (res.status === 200) {
          setMediaMetadata(res.data);
          formik.setFieldValue(
            "Streaming",
            res.data.live === "live" ? "Live" : "Non Live"
          );
          setDisableLive(true);
          setShowPlayer(true);
          setIsUrlLocked(true);

          if (isBlurValidation) return;
          const finalEmbedId =
            platform === "hls"
              ? extractUrlFromEmbed(formik.values.Embed_Url)
              : res.data.id;

          if (fromDetails && onMediaUpdate) {
            await onMediaUpdate(finalEmbedId, platform);
          } else if (onMediaCreation) {
            await onMediaCreation(res.data, finalEmbedId, platform);
          }
        }
      } catch (error) {
        console.error("Error validating URL:", error);
        if (isBlurValidation) {
          toast.fail("");
        }
      } finally {
        setIsValidatingUrl(false);
      }
    },
    [
      formik,
      embedType,
      mediaMetaApiPlatformMap,
      facebookChannelId,
      fromDetails,
      onMediaUpdate,
      onMediaCreation,
      MediaMetaApi,
      toast,
    ]
  );

  const handleClearUrl = useCallback(() => {
    setIsUrlLocked(false);
    setShowPlayer(false);
    setMediaMetadata(null);
    formik.setFieldValue("Embed_Url", "");
    formik.setFieldTouched("Embed_Url", false, false);
    formik.setFieldError("Embed_Url", "");
    setDisableLive(false);
  }, [formik]);

  const resetValidationState = useCallback(() => {
    setShowPlayer(false);
    setIsUrlLocked(false);
    setMediaMetadata(null);
    setDisableLive(false);
  }, []);

  return {
    mediaMetadata,
    showPlayer,
    isUrlLocked,
    isValidatingUrl,
    disableLive,
    validateUrl,
    handleClearUrl,
    resetValidationState,
    setMediaMetadata,
    setShowPlayer,
    setDisableLive,
  };
};
